import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown, FlaskConical, Leaf, Sprout } from 'lucide-react';
import { type Category } from './shopData';
import { ShopProductCard } from '../../ShopProductCard';
import { ReviewsSummary } from '../../ReviewsSummary';
import { useCart } from '../../../contexts/CartContext';
import { useBestSellers } from '../../../utils/useBestSellers';
import { useReviewStats } from '../../../utils/useReviewStats';
import { getStorefrontProducts } from '../../../utils/catalog';
import { useCatalogProducts } from '../../../utils/useCatalogProducts';
export type CollectionSlug = 'performance' | 'gut-health';
const ALL_CATEGORIES: Category[] = ['Protein', 'Strength', 'Endurance', 'Health', 'Stress', 'Sleep'];

/**
 * Collections are curated views of the same catalog: `categories` pulls whole
 * catalog categories in, `keywords` pulls in individual products whose copy
 * matches (used where the catalog has no matching category yet).
 */
const COLLECTIONS: Record<CollectionSlug, {
  title: string;
  blurb: string;
  categories: Category[];
  keywords: string[];
  emptyTitle: string;
  emptyCopy: string;
}> = {
  performance: {
    title: 'Performance',
    blurb: 'Protein, creatine, and endurance essentials at clinically studied doses — for strength, output, and recovery.',
    categories: ['Protein', 'Strength', 'Endurance'],
    keywords: [],
    emptyTitle: 'No performance products match those filters',
    emptyCopy: 'Clear your filters to see the full performance range, or browse every supplement in the shop.'
  },
  'gut-health': {
    title: 'Gut Health',
    blurb: 'Fiber, digestive support, and daily foundations for a calmer, more regular gut.',
    categories: [],
    keywords: ['gut', 'fiber', 'inulin', 'probiotic', 'prebiotic', 'digest', 'enzyme', 'bloat', 'microbiome'],
    emptyTitle: 'Gut health formulas are on the way',
    emptyCopy: 'This collection is being stocked right now. Browse the full shop in the meantime — new formulas appear here the moment they launch.'
  }
};
function searchableText(product: {
  name: string;
  tagline: string;
  shortDescription: string;
  categories: Category[];
}) {
  return [product.name, product.tagline, product.shortDescription, ...product.categories].join(' ').toLowerCase();
}
export function Shop({
  collection
}: {
  collection?: CollectionSlug;
}) {
  const view = collection ? COLLECTIONS[collection] : null;
  const [activeFilter, setActiveFilter] = useState<'All' | Category | 'Bestsellers'>('All');
  const [selectedCategories, setSelectedCategories] = useState<Set<Category>>(new Set());
  const [searchParams] = useSearchParams();
  const searchTerm = searchParams.get('search')?.trim().toLowerCase() ?? '';
  const {
    addItem
  } = useCart();
  const catalogProducts = useCatalogProducts();
  const allProducts = getStorefrontProducts(catalogProducts);
  const storefrontProducts = view ? allProducts.filter(product => {
    if (product.categories.some(category => view.categories.includes(category))) return true;
    const text = searchableText(product);
    return view.keywords.some(keyword => text.includes(keyword));
  }) : allProducts;
  const bestSellers = useBestSellers();
  const reviewStats = useReviewStats();
  const categoryCounts = storefrontProducts.reduce((acc, product) => {
    product.categories.forEach(category => {
      acc[category] = (acc[category] || 0) + 1;
    });
    return acc;
  }, {} as Record<Category, number>);

  // Only surface category controls that actually have products in this view.
  const visibleCategories = ALL_CATEGORIES.filter(category => (categoryCounts[category] || 0) > 0);
  const showCategoryControls = visibleCategories.length > 1;
  let filtered = storefrontProducts;
  if (activeFilter === 'Bestsellers') {
    filtered = storefrontProducts.filter(product => bestSellers.has(product.id));
  } else if (activeFilter !== 'All') {
    filtered = storefrontProducts.filter(product => product.categories.includes(activeFilter));
  }
  if (selectedCategories.size > 0) {
    filtered = filtered.filter(product => product.categories.some(category => selectedCategories.has(category)));
  }
  if (searchTerm) {
    filtered = filtered.filter(product => {
      const searchableContent = [product.name, product.tagline, product.shortDescription, ...product.categories].join(' ').toLowerCase();
      return searchableContent.includes(searchTerm);
    });
  }
  const toggleCategory = (category: Category) => {
    const next = new Set(selectedCategories);
    if (next.has(category)) next.delete(category);else next.add(category);
    setSelectedCategories(next);
  };
  return <div className="w-full min-h-screen bg-white" data-id="element-2863" data-mp="2iup9wgwvtkth">
      <HeroCarousel collection={collection} data-id="element-2864" data-mp="2dvcn1w60gdqw" />

      <section className="py-12 md:py-16 px-6" data-id="element-2865" data-mp="2dwnrzzb6zrjl">
        <div className="max-w-7xl mx-auto" data-id="element-2866" data-mp="2sjqoq3roxw5v">
          <div className="text-sm text-[#5A5A5A] mb-6" data-id="element-2867" data-mp="2sksc97pw8b4h">
            <Link to="/" className="hover:text-tealslate transition-colors underline decoration-[#E5E5E5] underline-offset-4" data-id="element-2868" data-mp="2ntk0w6506foo">
              Home
            </Link>{' '}
            {view && <>
                <span className="mx-2" data-id="element-2869" data-mp="2wlc8m92n7apk">/</span>
                <Link to="/shop" className="hover:text-tealslate transition-colors underline decoration-[#E5E5E5] underline-offset-4" data-id="element-2870" data-mp="3bil7dnu9zgrb">
                  Shop
                </Link>{' '}
              </>}
            <span className="mx-2" data-id="element-2871" data-mp="1h6y2f8ho5zx7">/</span>{' '}
            {view ? view.title : 'Shop All Supplements'}
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-[#1F1F1F] mb-4 tracking-tight" data-id="element-2872" data-mp="21u5y7oz5ge7d">
            {view ? view.title : 'Shop All Supplements'}
          </h1>
          <p className="text-base text-[#5A5A5A] mb-10 max-w-2xl" data-id="element-2873" data-mp="25vri3ua3psr2">
            {view ? view.blurb : 'Explore our full range of clean, science-backed supplements.'}
          </p>

          {showCategoryControls && <div className="flex flex-wrap gap-3 mb-12" data-id="element-2874" data-mp="2kta4wr37xvvv">
              {(['All', 'Bestsellers', ...visibleCategories] as const).map(filter => <button key={filter} type="button" onClick={() => setActiveFilter(filter as 'All' | Category | 'Bestsellers')} className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${activeFilter === filter ? 'bg-[#1F1F1F] text-white border border-[#1F1F1F]' : 'bg-white border border-[#E5E5E5] text-[#5A5A5A] hover:border-[#1F1F1F] hover:text-[#1F1F1F]'}`} data-id="element-2875" data-mp="2orzee8l4yta8">
                    {filter === 'All' ? 'All Products' : filter}
                  </button>)}
            </div>}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-10" data-id="element-2876" data-mp="hz9vfbedcrsw">
            <aside className={showCategoryControls ? 'lg:col-span-1' : 'hidden'} data-id="element-2877" data-mp="1flqryefa31dl">
              <h2 className="text-2xl font-bold text-[#1F1F1F] mb-6" data-id="element-2878" data-mp="1amnylr8nujlg">
                Filter products
              </h2>
              <details open className="group border-t border-[#E5E5E5] pt-5" data-id="element-2879" data-mp="15uw455mdvtq1">
                <summary className="cursor-pointer list-none flex items-center justify-between text-base font-bold text-[#1F1F1F] mb-4" data-id="element-2880" data-mp="10wujig9b7mcg">
                  Category
                  <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" data-id="element-2881" data-mp="1ye1ozrn9453s" />
                </summary>
                <div className="space-y-3 pb-5" data-id="element-2882" data-mp="11xp4zzkfho0d">
                  {visibleCategories.map(category => <label key={category} className="flex items-center justify-between cursor-pointer text-sm text-[#5A5A5A] hover:text-[#1F1F1F] transition-colors" data-id="element-2883" data-mp="16o5i1ikc77m7">
                      <span className="flex items-center gap-3" data-id="element-2884" data-mp="xx53kjstny8f">
                        <input type="checkbox" checked={selectedCategories.has(category)} onChange={() => toggleCategory(category)} className="w-4 h-4 rounded border-[#E5E5E5] text-[#1F1F1F] focus:ring-[#1F1F1F]" data-id="element-2885" data-mp="1llxv842evjx8" />
                        {category}
                      </span>
                      <span className="text-xs text-[#9A9A90]" data-id="element-2886" data-mp="7sds5r0fo5m">
                        ({categoryCounts[category] || 0})
                      </span>
                    </label>)}
                </div>
              </details>
            </aside>

            <div className={showCategoryControls ? 'lg:col-span-3' : 'lg:col-span-4'} data-id="element-2887" data-mp="3tve20y0396js">
              {filtered.length === 0 ? <div className="border border-dashed border-[#D8D8D0] bg-[#FAFAFA] px-6 py-14 text-center" data-id="element-2888" data-mp="6nitfl9xi6d9">
                  <h2 className="text-lg font-bold text-[#1F1F1F]" data-id="element-2889" data-mp="1qsbesekd0p7">
                    {view ? view.emptyTitle : 'No products found'}
                  </h2>
                  <p className="text-sm text-[#5A5A5A] mt-2 max-w-md mx-auto" data-id="element-2890" data-mp="363yw6v93s73s">
                    {view ? view.emptyCopy : 'Try a different category, or clear your filters to view the full collection.'}
                  </p>
                  {view && <Link to="/shop" className="inline-flex mt-6 px-5 py-2.5 bg-[#1F1F1F] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#3B3B3B] transition-colors rounded" data-id="element-2891" data-mp="2rejavuscctvh">
                      Shop All Supplements
                    </Link>}
                </div> : <div className={`grid grid-cols-1 sm:grid-cols-2 gap-6 ${showCategoryControls ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`} data-id="element-2892" data-mp="1tzsossrb7cyd">
                  {filtered.map(product => <ShopProductCard key={product.id} product={product} addItem={addItem} isBestseller={bestSellers.has(product.id)} reviewStats={reviewStats[product.id]} data-id="element-2893" data-mp="1l0p2lfwuihfn" />)}
                </div>}
            </div>
          </div>
        </div>
      </section>

      <ReviewsSummary data-id="element-2894" data-mp="12aphf444lmhi" />
    </div>;
}
const HERO_SLIDES = {
  shop: {
    bg: 'https://cdn.magicpatterns.com/uploads/jZM2tEohB8P3RXVY53wDZo/file_0000000050e471fd9b72564486af2c12.png',
    eyebrow: 'Science-backed. Dietitian approved.',
    headlineStart: 'Supplements that support your',
    headlineEnd: 'best life.',
    subhead: 'High-quality, evidence-based supplements to help you feel better, every day.',
    primaryCta: {
      label: 'Shop All Supplements',
      href: '#products'
    }
  },
  performance: {
    bg: 'https://cdn.magicpatterns.com/uploads/jZM2tEohB8P3RXVY53wDZo/file_0000000050e471fd9b72564486af2c12.png',
    eyebrow: 'Train hard. Recover harder.',
    headlineStart: 'Built for the work',
    headlineEnd: 'you put in.',
    subhead: 'Protein, creatine, and endurance essentials dosed the way the research says they work.',
    primaryCta: {
      label: 'Shop Performance',
      href: '#products'
    }
  },
  'gut-health': {
    bg: 'https://cdn.magicpatterns.com/uploads/jZM2tEohB8P3RXVY53wDZo/file_0000000050e471fd9b72564486af2c12.png',
    eyebrow: 'Digestion first.',
    headlineStart: 'Support that starts',
    headlineEnd: 'in your gut.',
    subhead: 'Fiber and digestive support formulas gentle enough to take every single day.',
    primaryCta: {
      label: 'Shop Gut Health',
      href: '#products'
    }
  }
};
function HeroCarousel({
  collection
}: {
  collection?: CollectionSlug;
}) {
  const HERO_SLIDE = collection ? HERO_SLIDES[collection] : HERO_SLIDES.shop;
  return <section className="relative min-h-[426px] md:h-[426px] px-6 pt-[91px] overflow-hidden" data-id="element-2895" data-mp="3jl86hfbv216c">
      <div className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: `url('${HERO_SLIDE.bg}')`
    }} data-id="element-2896" data-mp="2vtt09bgyqzpt" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/40 pointer-events-none" data-id="element-2897" data-mp="34tzcku0xdtne" />

      <div className="relative h-full max-w-7xl mx-auto flex items-center" data-id="element-2898" data-mp="2zuuzvtesldsb">
        <div className="w-full pt-2 md:pt-0" data-id="element-2899" data-mp="k3qf0y2qzffm">
          <p className="text-[10px] md:text-[11px] font-bold text-[#E8E4D8]/90 uppercase tracking-[0.25em] mb-3" data-id="element-2900" data-mp="k2f9aspqrxlv">
            {HERO_SLIDE.eyebrow}
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white leading-[1.05] mb-1" data-id="element-2901" data-mp="nledhbia59x4">
            {HERO_SLIDE.headlineStart}
          </h1>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#E8E4D8] italic leading-[1.05] mb-4" data-id="element-2902" data-mp="rlerww5dpl46">
            {HERO_SLIDE.headlineEnd}
          </h1>
          <p className="text-sm md:text-base text-white/80 mb-5 max-w-md leading-relaxed" data-id="element-2903" data-mp="inw27egsiily">
            {HERO_SLIDE.subhead}
          </p>
          <a href={HERO_SLIDE.primaryCta.href} className="inline-flex px-6 py-3 bg-tealslate text-white text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#4A5A5F] transition-colors" data-id="element-2904" data-mp="16ei9h3iyzyuw">
            {HERO_SLIDE.primaryCta.label}
          </a>

          <div className="grid grid-cols-3 gap-4 max-w-lg mt-6" data-id="element-2905" data-mp="11k4ciwontjo4">
            {[{
            icon: Leaf,
            label: 'Dietitian Formulated'
          }, {
            icon: FlaskConical,
            label: 'Premium Ingredients'
          }, {
            icon: Sprout,
            label: 'No Artificial Fillers'
          }].map(badge => <div key={badge.label} className="flex items-center gap-2 text-left" data-id="element-2906" data-mp="5hi4f4ubgufh">
                <badge.icon className="h-4 w-4 shrink-0 text-[#E8E4D8]" data-id="element-2907" data-mp="1d0jv3g4ud3n" />
                <span className="text-[10px] text-white/85 leading-tight" data-id="element-2908" data-mp="3sk3d00fj6tgi">
                  {badge.label}
                </span>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}